import { Typography, Input, Button, Tooltip, Tag, Progress } from 'antd';
import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { getTotalKeywordLength } from './useMemoryPreferences';

const { Title, Text } = Typography;
const { TextArea } = Input;

const MAX_KEYWORDS = 20;
const MAX_KEYWORD_LENGTH = 200;

type MemoryPreferencesFormProps = {
  title: string;
  titlePopoverContent: string;
  knowledgeBaseLabel: string;
  knowledgeBaseDescription: string;
  keywordLabel: string;
  keywordDescription: string;
  keywordPopoverContent: string;
  keywordPlaceholder: string;
  emptyKeywordsText: string;
  instructions: string;
  setInstructions: (value: string) => void;
  keywords: string[];
  currentKeywordInput: string;
  setCurrentKeywordInput: (value: string) => void;
  keywordError: string;
  handleAddKeyword: () => void;
  handleRemoveKeyword: (keyword: string) => void;
  disabled?: boolean;
};

const MemoryPreferencesForm = ({
  title,
  titlePopoverContent,
  knowledgeBaseLabel,
  knowledgeBaseDescription,
  keywordLabel,
  keywordDescription,
  keywordPopoverContent,
  keywordPlaceholder,
  emptyKeywordsText,
  instructions,
  setInstructions,
  keywords,
  currentKeywordInput,
  setCurrentKeywordInput,
  keywordError,
  handleAddKeyword,
  handleRemoveKeyword,
  disabled = false,
}: MemoryPreferencesFormProps) => {
  const totalCharsUsed = getTotalKeywordLength(keywords);
  const remainingChars = MAX_KEYWORD_LENGTH - totalCharsUsed;
  const isAtLimit = keywords.length >= MAX_KEYWORDS || remainingChars <= 0;
  const charPercent = Math.min(
    (totalCharsUsed / MAX_KEYWORD_LENGTH) * 100,
    100
  );
  const charColor =
    charPercent >= 90 ? '#d63232' : charPercent >= 70 ? '#f59e0b' : '#00d090';

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <Title level={4} className="m-0">
          {title}
        </Title>
        <Tooltip title={titlePopoverContent} placement="right">
          <InfoCircleOutlined className="text-gray-400 cursor-help" />
        </Tooltip>
      </div>

      <div className="p-4 border rounded-lg bg-gray-50 mb-4">
        <div className="text-black font-bold text-[18px] flex items-center mb-1">
          {knowledgeBaseLabel}
        </div>
        <Text type="secondary" className="mb-3 block">
          {knowledgeBaseDescription}
        </Text>
        <TextArea
          placeholder={disabled ? 'Enable memory to edit' : ''}
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          autoSize={{ minRows: 4, maxRows: 8 }}
          className="font-sans"
          disabled={disabled}
        />
      </div>

      <div className="p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="text-black font-bold text-[18px] flex items-center">
            {keywordLabel}
          </div>
          <Tooltip title={keywordPopoverContent} placement="right">
            <InfoCircleOutlined className="text-gray-400 cursor-help" />
          </Tooltip>
        </div>
        <Text type="secondary" className="mb-3 block">
          {keywordDescription}
        </Text>

        <div className="flex gap-2 mb-2">
          <Input
            placeholder={keywordPlaceholder}
            value={currentKeywordInput}
            onChange={(e) => setCurrentKeywordInput(e.target.value)}
            onPressEnter={handleAddKeyword}
            disabled={disabled || isAtLimit}
            status={keywordError ? 'error' : ''}
            className="font-sans"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddKeyword}
            disabled={disabled || isAtLimit}
          >
            Add
          </Button>
        </div>

        {keywordError && (
          <Text type="danger" className="text-xs block mb-2">
            {keywordError}
          </Text>
        )}

        <div className="flex flex-wrap gap-2 min-h-[28px] mb-4">
          {keywords.length > 0 ? (
            keywords.map((keyword) => (
              <Tag
                closable={!disabled}
                key={keyword}
                onClose={() => handleRemoveKeyword(keyword)}
                style={{
                  maxWidth: 'calc(100% - 16px)',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  height: 'auto',
                  lineHeight: '1.4',
                }}
              >
                {keyword}
              </Tag>
            ))
          ) : (
            <Text type="secondary" italic>
              {emptyKeywordsText}
            </Text>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Progress
            percent={charPercent}
            showInfo={false}
            strokeWidth={6}
            strokeColor={charColor}
            className="flex-1 m-0"
          />
          <Text
            className="text-xs whitespace-nowrap"
            style={{ color: charPercent >= 90 ? '#d63232' : '#808080' }}
          >
            {remainingChars} of {MAX_KEYWORD_LENGTH} chars remaining
          </Text>
        </div>
      </div>
    </div>
  );
};

export default MemoryPreferencesForm;
